/**
 * SelectField - Custom dropdown select field component
 *
 * Task: T-P4-D2-F4b-4
 * Feature: P4-D2-F4 Web App Forms UI
 *
 * Custom dropdown implementation that works reliably in Telegram WebView
 * on both iOS and Android. Native <select> elements have known issues
 * in Telegram's WebView environment.
 *
 * Features:
 * - Click outside to close
 * - Keyboard navigation (Escape to close)
 * - Touch-friendly 44px minimum touch targets
 * - Smooth open/close animations
 * - Preserves existing visual design
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import type { SelectFieldProps } from './types';
import { FieldWrapper } from './FieldWrapper';
import styles from './fields.module.css';

/**
 * Chevron down arrow icon for the dropdown trigger
 */
function ChevronDownIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/**
 * SelectField renders a custom dropdown select with options.
 * Replaces native <select> for Telegram WebView compatibility.
 */
export function SelectField({
  field,
  value,
  onChange,
  onBlur,
  error,
  style,
  nonInteractive,
}: SelectFieldProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const options = field.options ?? [];

  // Find the label for the currently selected value
  const selectedLabel = options.find((opt) => opt.value === value)?.label;

  // Handle clicking outside to close dropdown
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        onBlur();
      }
    }

    // Use mousedown/touchstart for immediate response
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onBlur]);

  // Handle escape key to close dropdown
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Toggle dropdown open/closed
  const handleTriggerClick = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // Handle option selection
  const handleOptionClick = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      setIsOpen(false);
      // Don't call onBlur here - matches native select behavior where blur
      // only fires when focus leaves the element, not on selection.
      // The click-outside handler will call onBlur if user closes without selecting.
      // Return focus to trigger for accessibility
      triggerRef.current?.focus();
    },
    [onChange]
  );

  // Handle keyboard navigation within dropdown
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isOpen) {
        // Open on arrow down or enter when closed
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setIsOpen(true);
        }
        return;
      }

      // Handle navigation when open
      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          const currentIndex = options.findIndex((opt) => opt.value === value);
          const nextIndex = Math.min(currentIndex + 1, options.length - 1);
          onChange(options[nextIndex].value);
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          const currentIndex = options.findIndex((opt) => opt.value === value);
          const prevIndex = Math.max(currentIndex - 1, 0);
          onChange(options[prevIndex].value);
          break;
        }
        case 'Enter':
        case ' ':
          event.preventDefault();
          setIsOpen(false);
          // Don't call onBlur here - same as click selection
          break;
        case 'Escape':
          event.preventDefault();
          setIsOpen(false);
          break;
      }
    },
    [isOpen, options, value, onChange, onBlur]
  );

  // Build trigger class names
  const triggerClasses = [
    styles.selectTrigger,
    isOpen && styles.isOpen,
    error && styles.hasError,
    !selectedLabel && styles.placeholder,
  ]
    .filter(Boolean)
    .join(' ');

  // Build dropdown class names
  const dropdownClasses = [styles.selectDropdown, isOpen && styles.isOpen]
    .filter(Boolean)
    .join(' ');

  return (
    <FieldWrapper
      fieldId={field.fieldId}
      label={field.label}
      required={field.required}
      helpText={field.helpText}
      error={error}
      style={style}
    >
      <div ref={containerRef} className={styles.selectContainer}>
        {/* Trigger button */}
        <button
          ref={triggerRef}
          type="button"
          id={field.fieldId}
          className={triggerClasses}
          onClick={nonInteractive ? undefined : handleTriggerClick}
          onKeyDown={nonInteractive ? undefined : handleKeyDown}
          onBlur={(e) => {
            // Only fire onBlur if focus is leaving the entire component
            // (not moving to dropdown options)
            if (!containerRef.current?.contains(e.relatedTarget as Node)) {
              onBlur();
            }
          }}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-disabled={nonInteractive || undefined}
          aria-invalid={!!error}
          aria-describedby={error ? `${field.fieldId}-error` : undefined}
          style={nonInteractive ? { pointerEvents: 'none' } : undefined}
        >
          {selectedLabel || field.placeholder || 'Select an option'}
          <span className={styles.selectArrow}>
            <ChevronDownIcon />
          </span>
        </button>

        {/* Dropdown options list */}
        <div
          className={dropdownClasses}
          role="listbox"
          aria-labelledby={field.fieldId}
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            const optionClasses = [
              styles.selectOption,
              isSelected && styles.isSelected,
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={optionClasses}
                onClick={() => handleOptionClick(option.value)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    </FieldWrapper>
  );
}
